import { Suspense, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import type { Prontuario, ProntuarioSection } from "@/api/db/medico";
import { PRONTUARIO_SECTIONS } from "@/api/medico/taxonomy";
import { ErrorBoundary } from "@/components/error-boundary";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Skeleton, SkeletonRows } from "@/components/ui/skeleton";
import { useMedicoRequeue, useProntuario } from "@/hooks/use-medico";
import { djs } from "@/lib/djs";
import { MedicoLabels } from "@/lib/labels/medico";

import { Milestones } from "./milestones";
import { RedFlags } from "./red-flags";
import { Relance } from "./relance";
import { Section } from "./section";
import { TranscriptModal } from "./transcript-modal";

const EVIDENCE_REF = /\[([a-z]):(\d+)\]/gu;

interface Excerpt {
	transcriptId: string;
	highlights: number[];
}

function resolveExcerpt(
	evidence: string,
	groupId: string | undefined,
	transcripts: Prontuario["transcripts"],
): Excerpt | null {
	const refs = [...evidence.matchAll(EVIDENCE_REF)].map((match) => ({
		prefix: match[1],
		index: Number(match[2]),
	}));

	const first = refs.at(0);

	if (first === undefined || groupId === undefined) {
		return null;
	}

	const parts = transcripts
		.filter((transcript) => transcript.groupId === groupId)
		.sort((a, b) => (a.recordedAt < b.recordedAt ? -1 : 1));

	const target = parts.at(first.prefix.codePointAt(0)! - 97);

	if (target === undefined) {
		return null;
	}

	return {
		transcriptId: target.transcriptId,
		highlights: refs.filter((ref) => ref.prefix === first.prefix).map((ref) => ref.index),
	};
}

function groupOfMilestone(origin: string) {
	return origin.startsWith("extraction:") ? origin.slice("extraction:".length) : undefined;
}

function Reextract({ medPatientId }: { medPatientId: number }) {
	const requeue = useMedicoRequeue();

	const run = () => {
		requeue.mutate(
			{ medPatientId },
			{
				onSuccess: (result) => {
					toast.success(MedicoLabels.reextract.success(result.queued));
				},
			},
		);
	};

	return (
		<ConfirmationDialog
			slot="reextract-trigger"
			tone="neutral"
			title={MedicoLabels.reextract.confirm}
			pendingLabel={MedicoLabels.reextract.pending}
			isPending={requeue.isPending}
			onConfirm={run}
			className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium shadow-control transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 [&_svg]:size-3.5"
		>
			<RefreshCw className="size-3.5" />
			{MedicoLabels.reextract.action}
		</ConfirmationDialog>
	);
}

function LinkedTranscripts({
	transcripts,
	onOpen,
}: {
	transcripts: Prontuario["transcripts"];
	onOpen: (transcriptId: string) => void;
}) {
	return (
		<div className="flex flex-col gap-2">
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				{MedicoLabels.linkedTranscripts.title}
			</p>

			{transcripts.length === 0 && (
				<p className="text-xs italic text-muted-foreground">
					{MedicoLabels.linkedTranscripts.empty}
				</p>
			)}

			{transcripts.map((transcript) => (
				<button
					key={transcript.transcriptId}
					type="button"
					onClick={() => onOpen(transcript.transcriptId)}
					className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-left text-sm transition-colors hover:border-accent"
				>
					<span className="min-w-0 flex-1 truncate text-foreground">{transcript.title}</span>

					{transcript.transcriptClass !== null && (
						<span className="rounded-md border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
							{MedicoLabels.transcriptClass[transcript.transcriptClass]}
						</span>
					)}

					<span className="font-mono text-xs text-muted-foreground">
						{djs(transcript.recordedAt).format("DD/MM/YYYY HH:mm")}
					</span>
				</button>
			))}
		</div>
	);
}

export function ProntuarioTab({ medPatientId }: { medPatientId: number }) {
	return (
		<ErrorBoundary fallback={<ProntuarioError />}>
			<Suspense fallback={<ProntuarioSkeleton />}>
				<ProntuarioData medPatientId={medPatientId} />
			</Suspense>
		</ErrorBoundary>
	);
}

function ProntuarioError() {
	return <p className="text-sm text-destructive">Não foi possível carregar o prontuário.</p>;
}

function ProntuarioSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<Skeleton className="h-6 w-48" />
			<Skeleton className="h-20 w-full" />
			<SkeletonRows rows={4} />
		</div>
	);
}

function ProntuarioData({ medPatientId }: { medPatientId: number }) {
	const prontuario = useProntuario(medPatientId);

	const [excerpt, setExcerpt] = useState<Excerpt | null>(null);

	const { name, status, sections, transcripts, relance, redFlags, milestones } = prontuario.data;

	const sectionByKey = new Map(sections.map((section) => [section.section, section]));

	const orderedSections = PRONTUARIO_SECTIONS.map((key) => sectionByKey.get(key)).filter(
		(section): section is ProntuarioSection => section !== undefined,
	);

	const open = (evidence: string, groupId: string | undefined) => {
		const resolved = resolveExcerpt(evidence, groupId, transcripts);

		if (resolved !== null) {
			setExcerpt(resolved);
		}
	};

	const groupOfEvidence = (evidence: string) => {
		for (const section of sections) {
			const owner = section.items.find(
				(item) => item.evidence === evidence && item.extraction_group !== undefined,
			);

			if (owner !== undefined) {
				return owner.extraction_group;
			}
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex flex-wrap items-center gap-2">
					<h1 className="text-lg font-semibold text-foreground">{name}</h1>

					{status === "awaiting_registry" && (
						<span className="rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-xs font-medium text-warning">
							{MedicoLabels.awaitingRegistryChip}
						</span>
					)}
				</div>

				<Reextract medPatientId={medPatientId} />
			</div>

			<div>
				<p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
					Relance
				</p>
				<Relance items={relance} />
			</div>

			<LinkedTranscripts
				transcripts={transcripts}
				onOpen={(transcriptId) => setExcerpt({ transcriptId, highlights: [] })}
			/>

			<RedFlags
				flags={redFlags}
				onOpenEvidence={(evidence) => open(evidence, groupOfEvidence(evidence))}
			/>

			<div className="flex flex-col gap-3">
				{orderedSections.map((section) => (
					<Section
						key={section.section}
						medPatientId={medPatientId}
						section={section}
						onOpenEvidence={open}
					/>
				))}
			</div>

			<Milestones
				milestones={milestones}
				onOpenEvidence={(evidence, origin) => open(evidence, groupOfMilestone(origin))}
			/>

			{excerpt !== null && (
				<TranscriptModal
					transcriptId={excerpt.transcriptId}
					highlights={excerpt.highlights}
					onClose={() => setExcerpt(null)}
				/>
			)}
		</div>
	);
}
